import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { beginSessionExpiry, endSessionExpiry } from '@/services/sessionExpiryGuard'

export interface CustomRequestConfig extends AxiosRequestConfig {
  version?: string
  params?: AxiosRequestConfig['params']
}

const ApiBaseURL = import.meta.env.VITE_API_BASE_URL
if (ApiBaseURL === undefined || ApiBaseURL === '') {
  throw new Error('VITE_API_BASE_URL is not defined in the environment variables')
}

export class ApiClient {
  protected readonly client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: ApiBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.client.interceptors.request.use((config) => {
      const authStore = useAuthStore()
      const token = authStore.token
      if (token !== null && token !== undefined) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        // 401 (expired or revoked token): clear state and force a redirect
        // to /signin. Awaiting logout guarantees the rejection only
        // propagates after storage is cleared, so router guards see a
        // logged-out state and no protected view ever renders. No toast.
        if (error.response?.status === 401 && beginSessionExpiry()) {
          try {
            // Lazy import to avoid a module-load cycle: apiClient -> useAuth ->
            // userStore -> organizationService -> apiClient, which left
            // `ApiClient` undefined when apiClient evaluated first (e.g. in the
            // BulkAddMembersModal test). useAuth is only needed here at runtime.
            const { useAuth } = await import('@/composables/useAuth')
            await useAuth().logout()
          } finally {
            endSessionExpiry()
          }
        }
        return Promise.reject(error)
      },
    )
  }


  private formatUrl(url: string, version: string = 'v1'): string {
    const cleanUrl = url.startsWith('/') ? url : `/${url}`
    return `/${version}${cleanUrl}`
  }

  protected get<T>(url: string, config?: CustomRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(this.formatUrl(url, config?.version), config)
  }

  protected post<T>(url: string, data?: unknown, config?: CustomRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.post<T>(this.formatUrl(url, config?.version), data, config)
  }

  protected put<T>(url: string, data?: unknown, config?: CustomRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.put<T>(this.formatUrl(url, config?.version), data, config)
  }

  protected patch<T>(url: string, data?: unknown, config?: CustomRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(this.formatUrl(url, config?.version), data, config)
  }

  protected delete<T>(url: string, config?: CustomRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(this.formatUrl(url, config?.version), config)
  }
}
